import React, { useState, useEffect } from 'react';
import { CreditCard, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BankAccountSelector - Reusable component for selecting a bank account
 * Used in Purchase Orders, Invoices, etc.
 * Requires companyId to be set first
 */
const BankAccountSelector = ({ companyId, value, onChange, required = false, disabled = false }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (companyId) {
      fetchBankAccounts();
    } else {
      setBankAccounts([]);
      setSelectedAccount(null);
    }
  }, [companyId]);

  useEffect(() => {
    if (value && bankAccounts.length > 0) {
      const account = bankAccounts.find(a => a.id === value);
      setSelectedAccount(account);
    }
  }, [value, bankAccounts]);

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/procurement/bank-accounts?company_id=${companyId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch bank accounts');
      const data = await response.json();
      setBankAccounts(data);

      // Auto-select default account if no value
      if (!value) {
        const defaultAccount = data.find(a => a.is_default);
        if (defaultAccount && onChange) {
          onChange(defaultAccount.id, defaultAccount);
        }
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const accountId = e.target.value;
    const account = bankAccounts.find(a => a.id === accountId);
    setSelectedAccount(account);
    if (onChange) {
      onChange(accountId, account);
    }
  };

  if (!companyId) {
    return (
      <div>
        <label className="block text-sm font-medium text-[#2B2D42] mb-2">
          Bank Account {required && <span className="text-red-500">*</span>}
        </label>
        <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-[#6C757D]">
          Please select a company first
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-[#2B2D42] mb-2">
        Bank Account {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <CreditCard className="h-5 w-5 text-[#6C757D]" />
        </div>
        
        <select
          value={value || ''}
          onChange={handleChange}
          required={required}
          disabled={disabled || loading}
          className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent appearance-none bg-white"
        >
          {loading ? (
            <option value="">Loading bank accounts...</option>
          ) : bankAccounts.length === 0 ? (
            <option value="">No bank accounts available</option>
          ) : (
            <>
              <option value="">Select a bank account</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.bank_name} - {account.currency}
                  {account.is_default && ' ★ Default'}
                </option>
              ))}
            </>
          )}
        </select>
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown className="h-5 w-5 text-[#6C757D]" />
        </div>
      </div>

      {/* Selected Account Preview */}
      {selectedAccount && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-[#2B2D42]">
                  {selectedAccount.bank_name}
                </p>
                <p className="text-xs text-[#6C757D] mt-1">
                  {selectedAccount.account_name}
                </p>
              </div>
              <span className="text-xs font-semibold text-[#00B4D8] px-2 py-1 bg-[#00B4D8] bg-opacity-10 rounded">
                {selectedAccount.currency}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
              <div>
                <p className="text-xs text-[#6C757D]">Account #</p>
                <p className="text-xs font-mono text-[#2B2D42]">
                  {selectedAccount.account_number}
                </p>
              </div>
              {selectedAccount.iban && (
                <div>
                  <p className="text-xs text-[#6C757D]">IBAN</p>
                  <p className="text-xs font-mono text-[#2B2D42]">
                    {selectedAccount.iban}
                  </p>
                </div>
              )}
              {selectedAccount.swift && (
                <div>
                  <p className="text-xs text-[#6C757D]">SWIFT</p>
                  <p className="text-xs font-mono text-[#2B2D42]">
                    {selectedAccount.swift}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountSelector;